import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useMemo } from 'react';
import useProductCompatibility from "../../../../Hooks/useProductCompatibility";
import helpers from '../../../../Utils/helpers';
import SkynetIcon from "../../../Fundamentals/SkynetIcon";
import ExpandableTable from "../../../Fundamentals/Tables/ExpandedableTable";
import Tooltip, { TooltipContent, TooltipTrigger } from "../../../Overlays/Tooltip";
import { ProductSummaryCard } from "../ProductSummary";
import { VossProductType } from '../../../../Constants/VOSS/vossProductType';
import { VossProductClassification } from '../../../../Constants/VOSS/vossProductClassification';
import orderActions from '../../../../Actions/orderActions';
import * as calcTypes from '../../../../Constants/priceCalculationTypes';
import Spinner from '../../../Fundamentals/Spinner/Spinner';

export function VossPriceSummaryCard({ offer, priceCalculation, priceLoading, order, product, editOrderline }) {
    const dispatch = useDispatch();
    const boundOrderActions = useMemo(() => bindActionCreators(orderActions, dispatch), [dispatch]);
    const priceCalcType = calcTypes.priceCalculationTypes.order;
    const compatibility = useProductCompatibility(offer?.productOfferId);
    const { t } = useTranslation();

    const mainItems = priceCalculation?.includedProductGroups || [];
    const addonItems = priceCalculation?.optionalProductGroups || [];
    const removedItems = priceCalculation?.removedProductGroups || [];
    const licenseItems = priceCalculation?.licenses || [];

    const renderTableRow = (item, removed, index) => {
        return <TableRow key={index} item={item} removed={removed} offer={offer} priceCalculation={priceCalculation} />
    }

    return (
        <ProductSummaryCard>
            {priceLoading ? (
                <div className="p-64">
                    <Spinner />
                </div>
            ) : (<>
            {mainItems.length > 0 && (
                <ProductSummaryCard.Table className={'table-active'}>
                    <ProductSummaryCard.Table.THeader>
                        <th className="col-6">{t('productsummary.included')}</th>
                        <th className="col-2">{t('productsummary.discountamount')}</th>
                        <th className="col-2">{t('productsummary.initialprice')}</th>
                        <th className="col-2">{t('productsummary.nextprice')}</th>
                    </ProductSummaryCard.Table.THeader>
                    <ProductSummaryCard.Table.TBody>
                        {mainItems.map((item, index) => (
                            renderTableRow(item, false, index)
                        ))}
                    </ProductSummaryCard.Table.TBody>
                </ProductSummaryCard.Table>
            )}
            {(addonItems.length > 0 || licenseItems.length > 0 || removedItems.length > 0) && (
                <ProductSummaryCard.Table className={'table-active'}>
                    <ProductSummaryCard.Table.THeader>
                        <th className="col" colSpan="4">{t('productsummary.optional')}</th>
                    </ProductSummaryCard.Table.THeader>
                    <ProductSummaryCard.Table.TBody>
                        {addonItems.map((item, index) => (
                            renderTableRow(item, false, index)
                        ))}
                        {licenseItems.map((item, index) => (
                            renderTableRow(item, false, index)
                        ))}
                        {removedItems.map((item, index) => (
                            renderTableRow(item, true, index)
                        ))}
                    </ProductSummaryCard.Table.TBody>
                </ProductSummaryCard.Table>
            )}
            <ProductSummaryCard.Table className="border-0 shadow-0">
                <ProductSummaryCard.Table.THeader>
                    <th className="col-6"></th>
                    <th className="col-2">{helpers.currencyFormatter(priceCalculation?.totalDiscountAmount)}</th>
                    <th className="col-2">{helpers.currencyFormatter(priceCalculation?.initialPrice)}</th>
                    <th className="col-2">{helpers.currencyFormatter(priceCalculation?.nextPrice)}</th>
                </ProductSummaryCard.Table.THeader>
            </ProductSummaryCard.Table>
            </>)}

            <ProductSummaryCard.CompatibilityWarning compatibility={compatibility} />

            <div className="d-flex d-flex flex-row justify-content-end my-24 mr-24 align-items-center">
                <ProductSummaryCard.Actions orderActions={boundOrderActions} order={order} product={product} editOrderline={editOrderline} priceCalcType={priceCalcType} compatibility={compatibility} />
            </div>
        </ProductSummaryCard>
    );
}

function TableRow({ item, removed, offer, priceCalculation }) {
    const { t } = useTranslation();
    const products = offer?.tiers?.find(tier => tier.id === priceCalculation?.tierId)?.productGroups?.find(pg => pg.id === item.id)?.products
        ?.filter(p => (p.type === VossProductType.Main || p.type === VossProductType.Addon) && p.classification !== VossProductClassification.RevenueCarrier);

    const renderDiscountTooltip = () => (
        <Tooltip>
            <TooltipTrigger className="max-w-max-content d-flex align-items-center">
                {<><span className="mr-6">{helpers.currencyFormatter(item.discountAmount)}</span><SkynetIcon icon={'vismaicon-sm vismaicon-info vismaicon-dynamic '}></SkynetIcon></>}
            </TooltipTrigger>
            <TooltipContent>
                <strong>{t('productsummary.withoutdiscount')}</strong><br /> <span>{helpers.currencyFormatter(item.amount)}</span>
            </TooltipContent>
        </Tooltip>
    );

    return (
        <ExpandableTable.Row>
            <ExpandableTable.Row.Expandable>
                <td className="col-6">
                    {(removed || item.fullQuantity <= 0) ? <s>{item.description}</s> : item.description} {item.fullQuantity > 0 && ` - ${item.fullQuantity}`}
                </td>
                <td className="col-2">{item.discountAmount > 0 ? renderDiscountTooltip() : helpers.currencyFormatter(item.discountAmount)}</td>
                <td className="col-2">{helpers.currencyFormatter(item.initialPrice)}</td>
                <td className="col-2">{helpers.currencyFormatter(item.nextPrice)}</td>
            </ExpandableTable.Row.Expandable>
            {products?.length > 0 && (
                <ExpandableTable.Row.Expanded>
                    {products.map((product, index) => (
                        <tr key={index}>
                            <td colSpan="4">
                                <span>{product.name}</span>
                            </td>
                        </tr>
                    ))}
                </ExpandableTable.Row.Expanded>
            )}
        </ExpandableTable.Row>
    );
}